import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import { buildRemovedBatches } from "./buildRemovedBatches";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { RemovedBatchHeader } from "./RemovedBatchHeader";
import { RemovedDeviceRow } from "./RemovedDeviceRow";
import type { RemovedBatch, RemovedDevice, RemovedDevicesProps } from "./types";

const positionsOf = (devices: RemovedDevice[]): { x: number; y: number }[] =>
  devices
    .filter((device) => device.x !== undefined && device.y !== undefined)
    .map((device) => ({ x: device.x as number, y: device.y as number }));

export const RemovedDevices = ({
  removed,
  groups = [],
  destinationInfoById,
  onRestore,
  onRestoreGroup,
  circuitOptions = [],
  onAssignGroup,
  onAssignDevice,
  onSpotlight,
  isOpen,
  onOpenChange,
}: RemovedDevicesProps) => {
  const { t } = useTranslation();
  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const spotlightRef = useRef(onSpotlight);
  spotlightRef.current = onSpotlight;
  // Never true on the first render, so mounting closed cannot take a spotlight
  // somebody else set.
  const wasOpenRef = useRef(false);

  const batches = useMemo(
    () => buildRemovedBatches({ removed, groups }),
    [removed, groups],
  );

  const pinned = useMemo(
    () => removed.find((device) => device.destinationId === pinnedId) ?? null,
    [removed, pinnedId],
  );

  // By id, never by index: the struck path is gone from the derived geometry,
  // so its old position now holds the device that followed it.
  const infoOf = (destinationId: string) =>
    destinationInfoById?.[destinationId];

  const spotlight = (devices: RemovedDevice[]) => {
    const positions = positionsOf(devices);
    onSpotlight?.(positions.length ? positions : null);
  };

  const clear = () => onSpotlight?.(null);

  // Leaving falls back to whatever he pinned, not to nothing: a pinned device
  // stays ringed while he works on the drawing.
  const spotlightAfterRow = (batch: RemovedBatch) => () =>
    spotlight(pinned ? [pinned] : batch.devices);

  const spotlightAfterSection = () => (pinned ? spotlight([pinned]) : clear());

  const unpin = (destinationIds: string[]) => {
    if (!pinnedId || !destinationIds.includes(pinnedId)) return;
    setPinnedId(null);
  };

  const restoreAll = () => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED, {
      action: "restored",
      deviceCount: removed.length,
    });
    setPinnedId(null);
    clear();
    removed.forEach((device) => onRestore(device.destinationId));
  };

  const restoreBatch = (batch: RemovedBatch) => () => {
    unpin(batch.devices.map((device) => device.destinationId));
    clear();
    if (onRestoreGroup && batch.ordinal > 0) {
      onRestoreGroup(batch.id);
      return;
    }
    batch.devices.forEach((device) => onRestore(device.destinationId));
  };

  const restoreDevice = (device: RemovedDevice) => () => {
    unpin([device.destinationId]);
    clear();
    onRestore(device.destinationId);
  };

  const assignBatch = (batch: RemovedBatch) => (circuitId: string) => {
    unpin(batch.devices.map((device) => device.destinationId));
    clear();
    onAssignGroup?.(batch.id, circuitId);
  };

  const assignDevice = (device: RemovedDevice) => (circuitId: string) => {
    unpin([device.destinationId]);
    clear();
    onAssignDevice?.(device.destinationId, circuitId);
  };

  const togglePin = (device: RemovedDevice) => () =>
    setPinnedId((current) =>
      current === device.destinationId ? null : device.destinationId,
    );

  // A closed section marks nothing on the plan. The panel closes this one for
  // him whenever he opens another, and a pinned device's ring would otherwise
  // stay on the drawing with nothing on screen explaining it.
  useEffect(() => {
    if (disclosure.isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    setPinnedId(null);
    spotlightRef.current?.(null);
  }, [disclosure.isOpen]);

  if (removed.length === 0) return null;

  return (
    <PanelSection
      testId="removed-devices"
      tone="removed"
      title={t("smartSurveyor.cablesBeta.removedDevicesTitle")}
      count={removed.length}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
    >
      {/* The cap that used to sit here is the SHELL's now: every open section
          is bounded the same way, so one long list cannot be the only one that
          leaves the runs nothing. */}
      <div data-testid="removed-groups">
        {batches.map((batch) => (
          <section
            key={batch.id}
            data-testid={`removed-group-${batch.id}`}
            onMouseEnter={() => spotlight(batch.devices)}
            onMouseLeave={spotlightAfterSection}
            className="border-b border-slate-100 last:border-b-0"
          >
            {/* Only when it says something the row below cannot: "Removal 5 ·
                1 device" above one device is a header for its own sake. */}
            {batch.devices.length > 1 && (
              <RemovedBatchHeader
                batch={batch}
                circuitOptions={circuitOptions}
                onRestore={restoreBatch(batch)}
                onAssign={onAssignGroup ? assignBatch(batch) : undefined}
              />
            )}
            <ul>
              {batch.devices.map((device) => (
                <RemovedDeviceRow
                  key={device.destinationId}
                  device={device}
                  name={
                    infoOf(device.destinationId)?.name || device.destinationId
                  }
                  iconDataUrl={infoOf(device.destinationId)?.iconDataUrl}
                  isPinned={pinnedId === device.destinationId}
                  circuitOptions={circuitOptions}
                  onPin={togglePin(device)}
                  onRestore={restoreDevice(device)}
                  onAssign={onAssignDevice ? assignDevice(device) : undefined}
                  onHoverIn={() => spotlight([device])}
                  onHoverOut={spotlightAfterRow(batch)}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* With one device out this is the row's own Put back under another name. */}
      {removed.length > 1 && (
        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={restoreAll}
            className="flex h-9 items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 text-[13px] font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-50"
          >
            <RotateCcw size={15} />
            {t("smartSurveyor.cablesBeta.restoreAll")}
          </button>
        </div>
      )}
    </PanelSection>
  );
};
